// src/components/kpi/KpiTrends.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./KpiTrends.css";

const NA = "N/A";

function toNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const AREA_LABELS = {
  "OVERALL CYBER RISK SCORE": "OVERALL CYBER RISK",
  "THIRD PARTY SUPPLIER MANAGEMENT": "THIRD PARTY SUPPLIER",
};

function displayAreaName(area) {
  const up = String(area || "").trim().toUpperCase();
  return AREA_LABELS[up] || up;
}

function compactDateTime(s) {
  if (!s) return NA;
  const str = String(s);
  return str.length >= 16 ? str.slice(0, 16) : str;
}

// Keep API values (Green/Amber/Red) but show friendly labels
function ragLabel(rag) {
  if (rag === "Green") return "Compliant";
  if (rag === "Amber") return "Partially";
  if (rag === "Red") return "Non-comp";
  return NA;
}

function ragClass(rag) {
  if (rag === "Green") return "kpi-rag-green";
  if (rag === "Amber") return "kpi-rag-amber";
  if (rag === "Red") return "kpi-rag-red";
  return "kpi-rag-na";
}

function ragToScore(rag) {
  // used only for mini bar height
  if (rag === "Green") return 1;
  if (rag === "Amber") return 0.66;
  if (rag === "Red") return 0.33;
  return 0;
}

function trendTagFromRags(rags, higherIsBetter = true) {
  const nums = (rags || []).map(ragToScore).filter((x) => x !== 0);
  if (nums.length < 3) return NA;

  const first = nums[0];
  const last = nums[nums.length - 1];
  const delta = last - first;

  const eps = 0.00001;
  if (Math.abs(delta) < eps) return "Stable";

  const improving = higherIsBetter ? delta > 0 : delta < 0;
  return improving ? "Improving" : "Watch";
}

function pickKnownKey(keys, wanted) {
  const set = new Set(keys || []);
  return set.has(wanted) ? wanted : null;
}

export default function KpiTrends({ country, period, months = 6 }) {
  const entity = String(country ?? "").trim().toUpperCase();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [trend, setTrend] = useState(null);

  const title = useMemo(() => {
    const e = entity || "";
    const p = period ? String(period) : "";
    if (!e && !p) return "Board Trend";
    if (!e) return `Board Trend - ${p}`;
    if (!p) return `Board Trend - ${e}`;
    return `Board Trend - ${e} - ${p}`;
  }, [entity, period]);

  useEffect(() => {
    if (!entity || !period) {
      setTrend(null);
      setErr("");
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setErr("");

        const url = `/api/kpi_trend.php?entity=${encodeURIComponent(entity)}&period=${encodeURIComponent(
          period
        )}&months=${encodeURIComponent(months)}`;

        const res = await fetch(url, { credentials: "include" });
        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data?.success) throw new Error(data?.error || `HTTP ${res.status}`);

        setTrend(data.data || null);
      } catch (e) {
        console.error("KPI trend load failed", e);
        setTrend(null);
        setErr(e?.message || "Failed to load trend");
      } finally {
        setLoading(false);
      }
    })();
  }, [entity, period, months]);

  const periods = trend?.periods || [];
  const areas = Array.isArray(trend?.areas) ? trend.areas : [];
  const boardSeries = Array.isArray(trend?.board_series) ? trend.board_series : [];
  const keys = Array.isArray(trend?.keys) ? trend.keys : [];
  const summary = trend?.summary || {};

  const lastBoard = boardSeries.length ? boardSeries[boardSeries.length - 1] : null;
  const prevBoard = boardSeries.length >= 2 ? boardSeries[boardSeries.length - 2] : null;

  const completionRate = summary?.completion_rate_pct ?? null;
  const missingPeriods = Array.isArray(summary?.missing_periods) ? summary.missing_periods : [];

  // Optional deep dive charts (kept as-is)
  const kMss = pickKnownKey(keys, "MSS_COMPLIANCE_PCT");
  const kPatchTime = pickKnownKey(keys, "PATCH_TIME_DAYS_AVG");
  const kIncidents = pickKnownKey(keys, "SECURITY_INCIDENTS");
  const kOpen = pickKnownKey(keys, "OPEN_HIGH_CRIT_ISSUES");

  const series = Array.isArray(trend?.series) ? trend.series : [];
  const deepCharts = useMemo(() => {
    const out = [];
    if (kMss)
      out.push({
        key: kMss,
        title: "MSS compliance",
        fmt: (v) => (toNum(v) === null ? NA : `${toNum(v).toFixed(1)}%`),
      });
    if (kPatchTime)
      out.push({
        key: kPatchTime,
        title: "Patch time (days)",
        fmt: (v) => (toNum(v) === null ? NA : String(parseInt(toNum(v), 10))),
      });
    if (kIncidents)
      out.push({
        key: kIncidents,
        title: "Incidents",
        fmt: (v) => (toNum(v) === null ? NA : String(parseInt(toNum(v), 10))),
      });
    if (kOpen)
      out.push({
        key: kOpen,
        title: "Open high/critical",
        fmt: (v) => (toNum(v) === null ? NA : String(parseInt(toNum(v), 10))),
      });
    return out.slice(0, 4);
  }, [kMss, kPatchTime, kIncidents, kOpen]);

  const deepData = useMemo(() => {
    const miniBars = (values) => {
      const nums = values
        .map((v) => (v === null || v === undefined || v === "" ? null : Number(v)))
        .map((n) => (Number.isFinite(n) ? n : null));

      const finite = nums.filter((n) => n !== null);
      if (!finite.length) return nums.map(() => 0);

      const min = Math.min(...finite);
      const max = Math.max(...finite);

      if (min === max) return nums.map((n) => (n === null ? 0 : 1));

      return nums.map((n) => {
        if (n === null) return 0;
        return (n - min) / (max - min);
      });
    };

    const byKey = {};
    for (const c of deepCharts) {
      const values = series.map((p) => (p ? p[c.key] : null));
      byKey[c.key] = { values, bars: miniBars(values) };
    }
    return byKey;
  }, [deepCharts, series]);

  return (
    <div className="kpi-trend-card">
      <div className="kpi-trend-header">
        <div className="kpi-trend-title">{title}</div>
        <div className="kpi-trend-subtitle">
          {loading ? `Loading ${months}-month trend...` : err ? err : `Board cockpit over last ${months} months`}
        </div>
      </div>

      <div className="kpi-trend-body">
        {!entity || !period ? (
          <div className="kpi-trend-empty">Select an entity and a period to see trends.</div>
        ) : err ? (
          <div className="kpi-trend-empty">{err}</div>
        ) : !trend ? (
          <div className="kpi-trend-empty">{loading ? "Loading..." : "No trend data yet."}</div>
        ) : (
          <>
            <div className="kpi-trend-metrics">
              {areas.map((a) => {
                const areaName = a?.board_area || "";
                const areaLast = lastBoard?.areas?.find((x) => x?.board_area === areaName) || null;
                const areaPrev = prevBoard?.areas?.find((x) => x?.board_area === areaName) || null;

                const rag = areaLast?.rag || null;
                const prevRag = areaPrev?.rag || null;

                const ragTrend = boardSeries.map((bp) => {
                  const ap = bp?.areas?.find((x) => x?.board_area === areaName);
                  return ap?.rag || null;
                });

                const tag = trendTagFromRags(ragTrend, true);

                // Representative KPI key for tooltip (first KPI in that area)
                const repKey = (Array.isArray(a?.kpis) && a.kpis.length ? a.kpis[0]?.kpi_key : null) || null;

                return (
                  <div key={areaName} className="kpi-trend-metric">
                    <div className="kpi-trend-metric-label">{displayAreaName(areaName)}</div>

                    <div className={`kpi-rag ${ragClass(rag)}`}>{ragLabel(rag)}</div>

                    {/* Keep ONLY this line */}
                    <div className="kpi-trend-metric-sub">
                      Trend: {tag}
                      {prevRag ? ` | M-1: ${ragLabel(prevRag)}` : ""}
                    </div>

                    {/* Bars pushed to the bottom of the tile */}
                    <div className="kpi-trend-bars" style={{ marginTop: "auto" }} aria-label={`${areaName} trend`}>
                      {periods.map((p, i) => {
                        const r = ragTrend[i] || null;
                        const score = ragToScore(r);
                        const h = Math.round(score * 100);

                        const repVal =
                          repKey && boardSeries[i]?.areas
                            ? boardSeries[i].areas
                                .find((x) => x?.board_area === areaName)
                                ?.kpis?.find((kk) => kk?.kpi_key === repKey)?.value
                            : null;

                        const tooltip = `${p} | Status: ${ragLabel(r)}${
                          repKey ? ` | ${repKey}: ${repVal === null || repVal === undefined ? NA : repVal}` : ""
                        }`;

                        return (
                          <div key={`${areaName}-${p}`} className="kpi-trend-bar-wrap">
                            <div className="kpi-trend-bar" title={tooltip} style={{ height: `${h}%` }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {!!deepCharts.length && (
              <div className="kpi-trend-charts">
                {deepCharts.map((c) => {
                  const d = deepData[c.key];
                  const values = d?.values || [];
                  const bars = d?.bars || [];

                  return (
                    <div key={c.key} className="kpi-trend-chart">
                      <div className="kpi-trend-chart-title">{c.title}</div>
                      <div className="kpi-trend-bars">
                        {bars.map((h, i) => {
                          const tooltip = `${periods[i]}: ${c.fmt(values[i])}`;
                          return (
                            <div key={`${c.key}-${i}`} className="kpi-trend-bar-wrap">
                              <div
                                className="kpi-trend-bar"
                                title={tooltip}
                                style={{ height: `${Math.round(h * 100)}%` }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="kpi-trend-footnote">
              Completion: {completionRate === null ? NA : `${completionRate}%`}
              {missingPeriods.length ? ` | Missing: ${missingPeriods.join(" | ")}` : ""}
              {" | "}Last update: {compactDateTime(summary.last_update_at)}
              {summary.last_update_by ? ` | ${summary.last_update_by}` : ""}
            </div>
          </>
        )}
      </div>
    </div>
  );
}